import {useEffect} from 'react'
import {useShallow} from 'zustand/react/shallow'
import {usePullRequestStore, type State} from './PullRequestStore'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'
import {getPathOwnership} from '../page-data/loaders/use-codeowners-data'
import {forceAnnouncementToScreenReaders} from '../utils/force-announcement-to-screen-reader'
import type {DiffFilterState} from './slices/diff-filter'
import type {SuggestionIdentifier} from '@github-ui/diff-lines/conversation-types'
import {changeGroupGenerationUnavailableReasons} from '../types/group-types'
import {CommentsPreference} from '@github-ui/diff-view-settings/types'
import {createMemoizedSelectorHook} from './create-memoized-selector-hook'
import {matchesDiffFilters} from '../helpers/diff-helpers'

const emptySelectedRange = {baseOid: undefined, headOid: undefined}

export const useComparisonInfo = () => usePullRequestStore(state => state.comparisonInfo)
export const useComparisonInfoActions = () => usePullRequestStore(state => state.comparisonInfoActions)
export const useIsSingleFileMode = () => usePullRequestStore(state => state.isSingleFileMode)

export const useConfigOpts = () => usePullRequestStore(state => state.configOpts)
export const useConfigOptsActions = () => usePullRequestStore(state => state.configOptsActions)

export const useCurrentUser = () => usePullRequestStore(useShallow(state => state.currentUser))
export const useCurrentUserActions = () => usePullRequestStore(state => state.currentUserActions)

export const useViewSettings = () => usePullRequestStore(state => state.viewSettings)
export const useViewSettingsActions = () => usePullRequestStore(state => state.viewSettingsActions)

export const useCommentsPreference = () =>
  usePullRequestStore(state => state.viewSettings?.commentsPreference ?? CommentsPreference.Visible)
export const useLineSpacingPreference = () => usePullRequestStore(state => state.viewSettings?.lineSpacing ?? 'relaxed')
export const useSplitPreference = () => usePullRequestStore(state => state.viewSettings?.splitPreference ?? 'unified')

// Combined selector that merges user data, settings, and config for external components
export const useCombinedCurrentUser = createMemoizedSelectorHook(
  [(s: State) => s.currentUser, (s: State) => s.configOpts, (s: State) => s.viewSettings],
  (currentUser, configOpts, viewSettings) => ({
    // Map FilesRoutePayloadUser fields to DiffUser type
    avatarURL: currentUser.currentUserAvatarUrl || '',
    login: currentUser.currentUserLogin || '',
    canComment: configOpts.userCanComment && currentUser.canComment,
    tabSize: currentUser.tabSize,
    hasCopilotAccess: currentUser.hasCopilotAccess,
    canApplySuggestion: currentUser.canApplySuggestion,

    // view settings
    commentsPreference: viewSettings?.commentsPreference ?? CommentsPreference.Visible,
    lineSpacing: viewSettings?.lineSpacing ?? 'relaxed',

    // config options
    splitPreference: configOpts.forceUnifiedView ? 'unified' : (viewSettings?.splitPreference ?? 'unified'),
    shouldDisplayComments: configOpts.useInlineComments ?? false,
    shouldDisplayEditOptions: configOpts.useEditOptions ?? false,
  }),
)

export const useHasCommitRange = () => usePullRequestStore(state => state.comparisonInfo.comparisonView !== 'FULL')
export const useSelectedRange = () =>
  usePullRequestStore(state => state.comparisonInfo.selectedRange || emptySelectedRange)

export const usePullRequestId = () => usePullRequestStore(state => state.pullRequest.id)
export const usePullRequest = () => usePullRequestStore(state => state.pullRequest)
export const usePullRequestActions = () => usePullRequestStore(state => state.pullRequestActions)
export const useCopilotDiffContext = () => usePullRequestStore(state => state.copilotDiffContext)

export const useRepository = () => usePullRequestStore(state => state.repository)

export const useDiffSummaries = () => usePullRequestStore(state => state.diffSummaries)

// Memoized selector for Object.values(diffSummaries) - shared across multiple hooks
export const useDiffSummariesArray = createMemoizedSelectorHook([s => s.diffSummaries], diffSummaries =>
  Object.values(diffSummaries),
)

export const useDiffSummaryCount = () => usePullRequestStore(s => useDiffSummariesArray.selector(s).length)

export const useDiffSummariesActions = () => usePullRequestStore(state => state.diffSummariesActions)

export const useDiffSummaryPaths = () =>
  usePullRequestStore(useShallow(s => useDiffSummariesArray.selector(s).map(summary => summary.path)))

export const useDiffSummary = (filePath?: string) =>
  usePullRequestStore(state => (filePath ? state.diffSummaries[filePath] : undefined))

export const useHiddenFiles = () => useHiddenData().hiddenFiles
export const useFilteredDiffSummaries = () => useHiddenData().filteredDiffSummaries

export const useProgressiveDiffEntryByPathDigest = (pathDigest: string) =>
  usePullRequestStore(state => state.entriesMap[pathDigest])

export const useGetProgressiveDiffEntryByPathDigest = () =>
  usePullRequestStore(state => state.getProgressiveDiffEntryByPathDigest)

export const useSelectedPathDigest = () => usePullRequestStore(state => state.selectedPathDigest)
export const useGetSelectedPathDigest = () => usePullRequestStore(state => state.getSelectedPathDigest)
export const useSelectedPathDigestIsLoaded = () =>
  usePullRequestStore(state =>
    state.selectedPathDigest ? state.entriesMap[state.selectedPathDigest]?.isLoaded === true : false,
  )
export const useUpdateSelectedPathDigest = () =>
  usePullRequestStore(state => state.progressiveDiffsActions.updateSelectedPathDigest)

export const useIsSelectedFile = (pathDigest: string | undefined) =>
  usePullRequestStore(state => {
    if (!pathDigest) return false
    return state.selectedPathDigest === pathDigest
  })

export const useMarkerAndDiffNavigationActions = () =>
  usePullRequestStore(state => state.markerAndDiffNavigationActions)
export const useNavigateToSelectedComment = () =>
  usePullRequestStore(state => state.markerAndDiffNavigationActions.navigateToSelectedComment)
export const useNavigateToSelectedAnnotation = () =>
  usePullRequestStore(state => state.markerAndDiffNavigationActions.navigateToSelectedAnnotation)
export const useOnCommentThreadDeletedFromDiffSummaries = () =>
  usePullRequestStore(state => state.diffSummariesActions.onCommentThreadDeleted)
export const useSelectedCommentId = () => usePullRequestStore(state => state.markerAndDiffNavigation.commentId)
export const useGetSelectedCommentId = () => usePullRequestStore(state => state.markerAndDiffNavigation.getCommentId)
export const useGetSelectedMarkerId = () => usePullRequestStore(state => state.markerAndDiffNavigation.getMarkerId)
export const useSelectedMarkerId = () => usePullRequestStore(state => state.markerAndDiffNavigation.markerId)

const useHiddenData = createMemoizedSelectorHook(
  [
    useDiffSummariesArray.selector,
    s => s.codeowners,
    s => s.diffFilter.state,
    s => s.changeGroupGeneration.changeGroups,
  ],
  (diffSummariesArray, codeowners, diffFilterState, changeGroups) => {
    const hiddenFiles = new Map<string, boolean>()
    const visibleFilePathDigests = new Map<string, boolean>()
    const filteredDiffSummaries: PullRequestFileTreeDiff[] = []

    const activeChangeGroup = changeGroups.find(group => group.id === diffFilterState.activeChangeGroupId)
    // This set will be used for evaluating if a path is in the active generated group
    const activeGroupPaths = activeChangeGroup?.hunkPositions
      ? new Set(Object.keys(activeChangeGroup.hunkPositions))
      : undefined

    for (const diffSummary of diffSummariesArray) {
      const isOwnedByViewer = getPathOwnership({
        diffPath: diffSummary.path,
        codeownersData: codeowners,
      }).isOwnedByViewer

      if (isDiffVisible(diffSummary, diffFilterState, isOwnedByViewer, activeGroupPaths)) {
        filteredDiffSummaries.push(diffSummary)
        hiddenFiles.set(diffSummary.path, false)
        visibleFilePathDigests.set(diffSummary.pathDigest, true)
      } else {
        hiddenFiles.set(diffSummary.path, true)
        visibleFilePathDigests.set(diffSummary.pathDigest, false)
      }
    }

    return {hiddenFiles, visibleFilePathDigests, filteredDiffSummaries}
  },
)

/**
 * Hook that announces filter results to screen readers when filters are interacted with.
 * Use this hook in components that manage filter UI to ensure accessibility.
 */
export const useFilterResultsAnnouncement = () => {
  const filteredCount = usePullRequestStore(s => useHiddenData.selector(s).filteredDiffSummaries.length)
  const filtersInteractedWith = usePullRequestStore(s => s.diffFilter.filtersInteractedWith)

  // Only announce filter results on user interaction (and not on page load).
  useEffect(() => {
    if (filtersInteractedWith) {
      const announcementText = `${filteredCount} file${filteredCount === 1 ? '' : 's'} remain${
        filteredCount === 1 ? 's' : ''
      }`
      forceAnnouncementToScreenReaders(announcementText, 150)
    }
  }, [filtersInteractedWith, filteredCount])
}

function isDiffVisible(
  diff: PullRequestFileTreeDiff,
  filters: DiffFilterState,
  isOwnedByViewer: boolean,
  activeChangeGroupPaths: Set<string> | undefined,
): boolean {
  // If an active Group is set and the diff path is not in the set, short circuit the remaining filters
  if (activeChangeGroupPaths && !activeChangeGroupPaths.has(diff.path)) return false

  return matchesDiffFilters(diff, filters, isOwnedByViewer)
}

export const useActiveChangeGroup = () =>
  usePullRequestStore(s => {
    const activeChangeGroupId = s.diffFilter.state.activeChangeGroupId
    if (activeChangeGroupId) return s.changeGroupGeneration.changeGroups.find(group => group.id === activeChangeGroupId)
  })

export const useActiveChangeGroupId = () => usePullRequestStore(s => s.diffFilter.state.activeChangeGroupId)

export const useChangeGroups = () => usePullRequestStore(s => s.changeGroupGeneration.changeGroups)
export const useChangeGroupGeneration = () => usePullRequestStore(s => s.changeGroupGeneration)

export const useChangeGroupGenerationUnavailableReason = () =>
  usePullRequestStore(s => {
    if (s.changeGroupGeneration.unavailableReason) {
      return s.changeGroupGeneration.unavailableReason
    }

    if (s.changeGroupGeneration.changeGroups.length < 1 && !s.changeGroupGeneration.latestChangeGroupOrchestration) {
      return changeGroupGenerationUnavailableReasons.notGeneratedYet
    }

    return null
  })
export const useChangeGroupGenerationChannel = () =>
  usePullRequestStore(s => s.changeGroupGeneration.changeGroupGenerationChannel)
export const useChangeGroupGenerationActions = () => usePullRequestStore(s => s.changeGroupGenerationActions)
export const useLatestChangeGroupOrchestration = () =>
  usePullRequestStore(s => s.changeGroupGeneration.latestChangeGroupOrchestration)

export const useCanAccessChangeGroups = () => usePullRequestStore(s => s.changeGroupGeneration.canAccessChangeGroups)
export const useShowChangeGroupsPreference = () => usePullRequestStore(s => s.showChangeGroupPreference)

export const useCodeownersActions = () => usePullRequestStore(s => s.codeownersActions)
export const useShouldLoadCodeowners = () => usePullRequestStore(s => s.shouldLoadCodeowners)

export const useCollapsedDiffStatusActions = () => usePullRequestStore(s => s.filesCollapsedStatusActions)
export const useCollapsedDiffStatus = (filePath: string) =>
  usePullRequestStore(s => s.collapsedFilesMap?.[filePath] ?? false)
export const useGetCollapsedDiffStatus = () => usePullRequestStore(state => state.getCollapsedDiffStatus)

export const useDiffEntry = (filePath?: string) => usePullRequestStore(s => (filePath ? s.diffEntries[filePath] : null))
export const useDiffEntries = () => usePullRequestStore(s => s.diffEntries)
export const useDiffEntriesCount = () => usePullRequestStore(s => Object.keys(s.diffEntries).length)
export const useDiffEntryActions = () => usePullRequestStore(s => s.diffEntriesActions)

export const useDiffEntriesDigestsWithFilter = createMemoizedSelectorHook(
  [s => useHiddenData.selector(s).visibleFilePathDigests, s => s.entriesOrder],
  (visibleFilePathDigests, entriesOrder) => {
    return entriesOrder.filter(pathDigest => visibleFilePathDigests.get(pathDigest) === true)
  },
)

export const useProgressiveDiffActions = () => usePullRequestStore(s => s.progressiveDiffsActions)

export const usePrefetchDiff = () => usePullRequestStore(s => s.progressiveDiffsActions.loadPriority)

export const useDiffFilterState = () => usePullRequestStore(state => state.diffFilter.state)
export const useDiffFilterActions = () => usePullRequestStore(state => state.diffFilterActions)
export const useIsFilteringActive = () =>
  usePullRequestStore(state => {
    const filterState = state.diffFilter.state
    return (
      filterState.filterText.length > 0 ||
      filterState.unselectedFileExtensions.size > 0 ||
      filterState.showOnlyOwnedFiles === true ||
      filterState.showDeletedFiles === false ||
      filterState.showOnlyManifestFiles === true ||
      filterState.showVendoredFiles === false ||
      filterState.showViewedFiles === false
    )
  })

export const useSettingsActions = () => usePullRequestStore(s => s.settingsActions)

export const useViewedFileStatus = (filePath: string) =>
  usePullRequestStore(s => s.summariesViewedStatus?.[filePath] ?? false)
export const useViewedFilesCount = () => usePullRequestStore(s => s.summariesViewedCount)

export const useSuggestionBatch = () => usePullRequestStore(state => state.suggestionBatch)
export const useSuggestionBatchActions = () => usePullRequestStore(state => state.suggestionBatchActions)
export const useIsSuggestionBatchPending = () =>
  usePullRequestStore(state => state.suggestionBatch.isSuggestionBatchPending)
export const useSuggestionInBatch = (suggestionId: SuggestionIdentifier) =>
  usePullRequestStore(state =>
    state.suggestionBatch.activeBatch.some(
      id =>
        id.commentDatabaseId === suggestionId.commentDatabaseId && id.suggestionIndex === suggestionId.suggestionIndex,
    ),
  )

// eslint-disable-next-line no-barrel-files/no-barrel-files
export {
  useAnnotations,
  useAnnotationsPageInfo,
  useMarkers,
  useAllMarkers,
  useThreads,
  useGetThread,
  useGetThreadFromThreadID,
  useLayoutBaseRepository,
  useLayoutPullRequestId,
  useLayoutPullRequestNumber,
  useLayoutPullRequestTitle,
  useThread,
  useMarkersActions,
  useThreadSizeInfoMap,
  useUnresolvedThreadCount,
  useThreadCount,
  useMarkersAtPosition,
  usePendingReviewActions,
  usePendingReviews,
  useIsCommentBatchPending,
  usePullRequestBasePath,
  useStoredCommentAtPosition,
  useGetUpdatePersistedComment,
  useSetStoredCommentsForFile,
  useUnresolvedConversationCount,
  useUnresolvedConversationCountActions,
  useAnnotationsCount,
  useMarkerCountsActions,
  useIsComparisonOutdated,
  useComparisonActions,
  useIsInUnifiedBatch,
  useThreadHasBatchedSuggestion,
  useUnifiedBatchActions,
} from './layout-selectors'
// eslint-disable-next-line no-barrel-files/no-barrel-files
export type {ThreadSizeInfo, ThreadSizeInfoMap} from './layout-selectors'
